import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { ShieldCheck, Printer, Mail, Phone, MapPin } from "lucide-react";
import { POLICY_SECTIONS, POLICY_EFFECTIVE } from "@/data/privacyPolicy";

const FONT_URL = "https://fonts.googleapis.com/css2?family=Manrope:wght@400;500;600;700;800&display=swap";

const ContactCard = () => (
  <div data-testid="privacy-contact-card" className="mt-4 rounded-xl border border-slate-200 bg-white p-6 space-y-3">
    <p className="font-bold text-slate-900">Red Ball Express Logistics</p>
    <a data-testid="privacy-contact-email" href="mailto:billing@redballxpress.us?subject=Privacy%20Request" className="flex items-center gap-3 text-slate-700 hover:text-red-700 transition-colors">
      <Mail className="w-4 h-4 text-red-600" /> billing@redballxpress.us
    </a>
    <a data-testid="privacy-contact-phone" href="tel:9893915380" className="flex items-center gap-3 text-slate-700 hover:text-red-700 transition-colors">
      <Phone className="w-4 h-4 text-red-600" /> (989) 391-5380
    </a>
    <p className="flex items-center gap-3 text-slate-700">
      <MapPin className="w-4 h-4 text-red-600" /> 30 N Gould St #58539, Sheridan, WY 82801, USA
    </p>
  </div>
);

const Block = ({ block }) => {
  if (block.h3) return <h3 className="text-lg font-bold text-slate-900 mt-8 mb-3">{block.h3}</h3>;
  if (block.contact) return <ContactCard />;
  if (block.ul)
    return (
      <ul className="space-y-2.5 my-4">
        {block.ul.map((li) => (
          <li key={li.slice(0, 40)} className="relative pl-4 before:absolute before:left-0 before:top-[0.6em] before:w-1.5 before:h-1.5 before:rounded-full before:bg-red-600">
            {li}
          </li>
        ))}
      </ul>
    );
  return <p className="my-4">{block.p}</p>;
};

const useActiveSection = () => {
  const [active, setActive] = useState(POLICY_SECTIONS[0].id);
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => e.isIntersecting && setActive(e.target.id)),
      { rootMargin: "-120px 0px -65% 0px" }
    );
    POLICY_SECTIONS.forEach((s) => { const el = document.getElementById(s.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
  }, []);
  return active;
};

const TableOfContents = ({ active }) => (
  <nav data-testid="privacy-toc" className="lg:sticky lg:top-28 print:hidden">
    <p className="text-xs font-bold tracking-wider text-slate-500 uppercase mb-3">Contents</p>
    <ol className="border-l border-slate-200">
      {POLICY_SECTIONS.map((s) => (
        <li key={s.id}>
          <a
            href={`#${s.id}`}
            data-testid={`privacy-toc-${s.id}`}
            className={`block -ml-px border-l-2 pl-4 py-1.5 text-sm transition-colors ${active === s.id ? "border-red-600 text-red-700 font-medium" : "border-transparent text-slate-500 hover:text-slate-900"}`}
          >
            {s.title}
          </a>
        </li>
      ))}
    </ol>
  </nav>
);

export default function PrivacyPolicyPage() {
  const active = useActiveSection();
  return (
    <div data-testid="privacy-policy-page" className="bg-white text-slate-700" style={{ fontFamily: "Manrope, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif" }}>
      <Helmet><link rel="stylesheet" href={FONT_URL} /></Helmet>
      <header className="border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-14 sm:py-20">
          <div className="flex items-start justify-between gap-4">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-3 py-1 text-xs font-bold tracking-wider text-red-700 uppercase">
              <ShieldCheck className="w-3.5 h-3.5" /> Privacy
            </span>
            <button data-testid="privacy-print-btn" onClick={() => window.print()} className="print:hidden inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-50 transition-colors">
              <Printer className="w-4 h-4" /> Print
            </button>
          </div>
          <h1 data-testid="privacy-title" className="mt-5 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-900">Privacy Policy</h1>
          <p className="mt-5 max-w-2xl text-base md:text-lg text-slate-600">
            How Red Ball Express Logistics collects, uses, shares, and protects information about the shippers, carriers, drivers, and visitors we work with every day.
          </p>
          <p data-testid="privacy-effective-date" className="mt-5 text-xs text-slate-500">Effective {POLICY_EFFECTIVE} · Last updated {POLICY_EFFECTIVE}</p>
        </div>
      </header>
      <div className="bg-slate-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-[240px_1fr] gap-10 lg:gap-14">
          <aside><TableOfContents active={active} /></aside>
          <article className="leading-relaxed text-[15px] sm:text-base min-w-0">
            {POLICY_SECTIONS.map((s, i) => (
              <section key={s.id} id={s.id} data-testid={`privacy-section-${s.id}`} className="scroll-mt-28 mb-12">
                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 mb-2">{i + 1}. {s.title}</h2>
                {s.blocks.map((b, j) => <Block key={j} block={b} />)}
              </section>
            ))}
          </article>
        </div>
      </div>
    </div>
  );
}
